import { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Video as VideoIcon, VideoOff, Shield, Maximize2, Minimize2 } from 'lucide-react';
import type { Participant } from '../../stores/participantsStore';
import { useRoom } from '../../contexts/RoomContext';

interface VideoTileProps {
  participant: Participant;
  isLocal?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  isCurrentUserHost?: boolean;
}

const VideoTile = ({ participant, isLocal = false, isExpanded = false, onToggleExpand, isCurrentUserHost = false }: VideoTileProps) => {
  const { muteParticipant } = useRoom();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasVideoTrack, setHasVideoTrack] = useState(false);

  useEffect(() => {
    const videoElement = videoRef.current;
    
    if (!videoElement) return;

    // Если идет демонстрация экрана - создаем комбинированный поток
    // с видео из screenStream и аудио из обычного stream
    let activeStream: MediaStream | null = null;
    
    if (participant.screenShareEnabled && participant.screenStream) {
      // Создаем новый MediaStream с видео из screenStream и аудио из обычного stream
      const combinedStream = new MediaStream();
      
      // Добавляем видео треки из screenStream
      const screenVideoTracks = participant.screenStream.getVideoTracks();
      screenVideoTracks.forEach(track => combinedStream.addTrack(track));
      
      // Добавляем аудио треки из обычного stream (если есть)
      if (participant.stream) {
        const audioTracks = participant.stream.getAudioTracks();
        audioTracks.forEach(track => combinedStream.addTrack(track));
      }
      
      activeStream = combinedStream;
      console.log(`[VideoTile] Created combined stream for ${participant.displayName}:`, {
        videoTracks: screenVideoTracks.length,
        audioTracks: participant.stream?.getAudioTracks().length || 0
      });
    } else {
      // Обычный режим - используем stream участника
      activeStream = participant.stream || null;
    }
    
    if (activeStream) {
      console.log(`[VideoTile] Setting stream for ${participant.displayName}:`, {
        id: participant.id,
        isLocal,
        isScreenShare: participant.screenShareEnabled,
        tracks: activeStream.getTracks().map(t => ({ kind: t.kind, enabled: t.enabled, readyState: t.readyState, label: t.label })),
      });
      
      videoElement.srcObject = activeStream;
      
      // Проверяем наличие video track
      const videoTracks = activeStream.getVideoTracks();
      setHasVideoTrack(videoTracks.length > 0 && videoTracks.some(t => t.readyState === 'live'));
      
      // Обработка ошибок воспроизведения
      videoElement.onloadedmetadata = () => {
        console.log(`[VideoTile] Video metadata loaded for ${participant.displayName}`);
        videoElement.play().catch(err => {
          console.warn(`[VideoTile] Autoplay failed for ${participant.displayName}:`, err);
        });
      };
      
      videoElement.onerror = (e) => {
        console.error(`[VideoTile] Video error for ${participant.displayName}:`, e);
      };
    }
    
    return () => {
      if (videoElement) {
        videoElement.srcObject = null;
      }
    };
  }, [participant.stream, participant.screenStream, participant.screenShareEnabled, participant.displayName, participant.id, isLocal]);

  // Проверяем реальное наличие активных треков в stream
  const hasActiveAudioTrack = participant.stream?.getAudioTracks().some(t => t.readyState === 'live') || false;
  const hasActiveVideoTrack = participant.stream?.getVideoTracks().some(t => t.readyState === 'live') || false;

  // Определяем, показывать ли видео
  const showVideo = participant.stream && (hasVideoTrack || participant.videoEnabled);

  // Определяем цвет рамки в зависимости от активности
  const borderColor = participant.isSpeaking 
    ? 'border-green-500' 
    : 'border-gray-700 hover:border-blue-500';

  return (
    <div className={`group relative bg-gray-900 rounded-lg overflow-hidden aspect-video border-2 ${borderColor} transition-all h-full`}>
      {/* Video Element - всегда рендерим, но скрываем если нет видео */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isLocal}
        className={`w-full h-full object-contain ${showVideo ? '' : 'hidden'}`}
      />
      
      {/* Placeholder когда нет видео */}
      {!showVideo && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-gray-700 flex items-center justify-center mb-4 mx-auto">
              <span className="text-3xl font-bold text-white">
                {participant.displayName.charAt(0).toUpperCase()}
              </span>
            </div>
            <p className="text-white text-xl font-medium">{participant.displayName}</p>
            <VideoOff className="w-8 h-8 text-gray-500 mt-4 mx-auto" />
          </div>
        </div>
      )}

      {/* Overlay с информацией */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {participant.isHost && (
              <Shield className="w-4 h-4 text-blue-400" />
            )}
            <span className="text-white text-sm font-medium truncate max-w-[150px]">
              {participant.displayName}
              {isLocal && ' (Вы)'}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {participant.audioEnabled && hasActiveAudioTrack ? (
              <div className="bg-green-600/80 p-1.5 rounded-full">
                <Mic className="w-3.5 h-3.5 text-white" />
              </div>
            ) : (
              <div className="bg-red-600/80 p-1.5 rounded-full">
                <MicOff className="w-3.5 h-3.5 text-white" />
              </div>
            )}

            {participant.videoEnabled && hasActiveVideoTrack ? (
              <VideoIcon className="w-4 h-4 text-green-400" />
            ) : (
              <VideoOff className="w-4 h-4 text-red-400" />
            )}
          </div>
        </div>
      </div>

      {/* Поднятая рука */}
      {participant.raisedHand && (
        <div className="absolute top-3 right-3 bg-yellow-500 p-2 rounded-full animate-bounce-soft">
          <span className="text-2xl">✋</span>
        </div>
      )}

      {/* Индикатор говорения (можно добавить позже) */}
      {participant.audioEnabled && (
        <div className="absolute top-3 left-3">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
        </div>
      )}

      {/* Кнопка раскрытия/сворачивания */}
      {onToggleExpand && (
        <button
          onClick={onToggleExpand}
          className="absolute top-3 right-3 bg-black/60 hover:bg-black/80 p-2 rounded-lg transition-all opacity-0 group-hover:opacity-100"
          title={isExpanded ? 'Свернуть' : 'Развернуть'}
        >
          {isExpanded ? (
            <Minimize2 className="w-4 h-4 text-white" />
          ) : (
            <Maximize2 className="w-4 h-4 text-white" />
          )}
        </button>
      )}

      {/* Кнопка отключения микрофона для администратора */}
      {isCurrentUserHost && !isLocal && !participant.isHost && participant.audioEnabled && (
        <button
          onClick={() => muteParticipant(participant.id)}
          className="absolute top-3 left-12 bg-red-600/80 hover:bg-red-600 p-2 rounded-lg transition-all opacity-0 group-hover:opacity-100"
          title="Отключить микрофон участника"
        >
          <MicOff className="w-4 h-4 text-white" />
        </button>
      )}
    </div>
  );
};

export default VideoTile;
